import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

export interface ExportColumn {
	title: string;
	dataIndex: string;
	render?: (value: any, row: any) => string;
}

const getSvgAsImage = async (svgPath: string): Promise<string> => {
	return new Promise((resolve, reject) => {
		fetch(svgPath)
			.then((res) => res.text())
			.then((svgText) => {
				const svgBlob = new Blob([svgText], { type: "image/svg+xml" });
				const url = URL.createObjectURL(svgBlob);

				const img = new Image();
				img.onload = () => {
					const canvas = document.createElement("canvas");
					canvas.width = img.width;
					canvas.height = img.height;

					const ctx = canvas.getContext("2d");
					if (!ctx) {
						console.error("Canvas context not available");
						reject(new Error("Canvas context not available"));
						return;
					}

					ctx.drawImage(img, 0, 0);
					const pngBase64 = canvas.toDataURL("image/png");
					resolve(pngBase64);
					URL.revokeObjectURL(url);
				};

				img.onerror = (e) => {
					console.error("Image failed to load", e);
					reject(new Error("Failed to load logo image"));
				};

				img.src = url;
			})
			.catch((err) => {
				console.error("Error fetching SVG:", err);
				reject(new Error("SVG fetch failed"));
			});
	});
};

// Helper function to add government header (adjusted for landscape)
const addGovernmentHeader = async (doc: jsPDF, reportTitle: string = "Monthly School Status Report: National", month: string = "May 2025") => {
	try {
		// Add left logo (Government of India emblem)
		const leftLogoPath = "/assets/img/National-Emblem.svg"; // Government emblem
		const leftLogoBase64 = await getSvgAsImage(leftLogoPath);
		doc.addImage(leftLogoBase64, "PNG", 15, 10, 20, 25);
	} catch (err) {
		console.warn("Left logo loading failed", err);
	}

	try {
		// Add right logo (PM POSHAN logo) - adjusted position for landscape
		const rightLogoPath = "/assets/img/logo-1.svg"; // PM POSHAN logo
		const rightLogoBase64 = await getSvgAsImage(rightLogoPath);
		doc.addImage(rightLogoBase64, "PNG", doc.internal.pageSize.getWidth() - 35, 10, 20, 25);
	} catch (err) {
		console.warn("Right logo loading failed", err);
	}

	// Main title - Pradhan Mantri Poshan Shakti Nirman
	doc.setFontSize(16);
	doc.setFont("helvetica", "bold");
	const mainTitle = "Pradhan Mantri Poshan Shakti Nirman";
	const mainTitleWidth = doc.getTextWidth(mainTitle);
	doc.text(mainTitle, (doc.internal.pageSize.getWidth() - mainTitleWidth) / 2, 20);

	// Subtitle - (PM POSHAN)
	doc.setFontSize(12);
	doc.setFont("helvetica", "normal");
	const subtitle = "(PM POSHAN)";
	const subtitleWidth = doc.getTextWidth(subtitle);
	doc.text(subtitle, (doc.internal.pageSize.getWidth() - subtitleWidth) / 2, 27);

	// Department info
	doc.setFontSize(10);
	const deptInfo = "Department Of School Education & Literacy | Ministry Of Education, Government Of India";
	const deptInfoWidth = doc.getTextWidth(deptInfo);
	doc.text(deptInfo, (doc.internal.pageSize.getWidth() - deptInfoWidth) / 2, 34);

	// AMS info
	doc.setFontSize(9);
	const amsInfo = "| Automated Monitoring System (AMS)";
	const amsInfoWidth = doc.getTextWidth(amsInfo);
	doc.text(amsInfo, (doc.internal.pageSize.getWidth() - amsInfoWidth) / 2, 40);

	// Report details section - ALL ON SAME LINE with proper alignment
	doc.setFontSize(10);
	doc.setFont("helvetica", "normal");
	doc.setTextColor(0, 0, 0);

	const pageWidth = doc.internal.pageSize.getWidth();
	const currentDate = new Date();
	const formattedDate = currentDate.toLocaleDateString("en-US", {
		weekday: "long",
		year: "numeric",
		month: "long",
		day: "numeric"
	});
	const formattedTime = currentDate.toLocaleTimeString("en-US", {
		hour12: true,
		hour: "2-digit",
		minute: "2-digit"
	});

	const yPosition = 55;

	// LEFT SIDE - Report Title and Month
	let leftX = 15;

	// Report Title
	doc.setFont("helvetica", "bold");
	doc.text(reportTitle, leftX, yPosition);
	leftX += doc.getTextWidth(reportTitle) + 8;

	// Month label and value
	doc.setFont("helvetica", "normal");
	doc.setTextColor(100);
	doc.text("Month:", 150, yPosition);
	leftX += doc.getTextWidth("Month:") + 65;

	doc.setTextColor(0, 0, 0);
	doc.text(month, leftX, yPosition);

	// RIGHT SIDE - Generated On
	const generatedLabel = "Report Generated On:";
	const generatedText = `${formattedDate}, ${formattedTime}`;
	const totalRightWidth = doc.getTextWidth(generatedLabel) + 3 + doc.getTextWidth(generatedText);
	const rightX = pageWidth - totalRightWidth - 15; // 15mm margin from right edge

	doc.setTextColor(100);
	doc.text(generatedLabel, rightX, yPosition);

	doc.setTextColor(0, 0, 0);
	doc.text(generatedText, rightX + doc.getTextWidth(generatedLabel) + 3, yPosition);

	return 70;
};

// Enhanced PDF export function with landscape orientation
export const exportToPDF = async (
	data: any[],
	columns: ExportColumn[],
	filename = "report.pdf",
	options: {
		reportTitle?: string;
		month?: string;
		additionalInfo?: string;
	} = {}
) => {
	// Change to landscape orientation
	const doc = new jsPDF({
		orientation: 'landscape',
		unit: 'mm',
		format: 'a4'
	});

	const {
		reportTitle = "Monthly School Status Report: National",
		month = "May 2025",
		additionalInfo
	} = options;

	doc.setFontSize(8);

	// Add government header
	const startY = await addGovernmentHeader(doc, reportTitle, month);

	// Add additional info if provided
	let contentStartY = startY;
	if (additionalInfo) {
		doc.setFontSize(10);
		doc.setFont("helvetica", "normal");
		doc.text(additionalInfo, 15, contentStartY);
		contentStartY += 10;
	}

	// Prepare table data
	const headers = [columns.map((col) => col.title)];
	const rows = data.map((row) =>
		columns.map((col) => {
			const value = row[col.dataIndex];
			return col.render ? col.render(value, row) : value ?? "";
		})
	);

	// Add table with grey header and black/white styling
	autoTable(doc, {
		startY: contentStartY,
		head: headers,
		body: rows,
		styles: {
			fontSize: 8,
			cellPadding: 3,
			lineColor: [0, 0, 0],
			lineWidth: 0.2,
			textColor: [0, 0, 0],
			fillColor: [255, 255, 255],
			valign: 'middle',
			halign: 'left',
		},
		headStyles: {
			fillColor: [233, 237, 244],
			textColor: [0, 0, 0],
			fontStyle: 'bold',
			lineColor: [0, 0, 0],
			lineWidth: 0.2,
			valign: 'middle',
			halign: 'center',
		},
		alternateRowStyles: {
			fillColor: [255, 255, 255],
			valign: 'middle',
		},
		tableLineColor: [0, 0, 0],
		tableLineWidth: 0.2,
		margin: { left: 15, right: 15 },
		didDrawPage: (data: any) => {
			const pageCount = doc.getNumberOfPages();
			doc.setFontSize(8);
			doc.setFont("helvetica", "normal");
			doc.setTextColor(0, 0, 0);
			doc.text(
				`Page ${data.pageNumber} of ${pageCount}`,
				doc.internal.pageSize.getWidth() - 30,
				doc.internal.pageSize.getHeight() - 10
			);
		},
		didParseCell: (data: any) => {
			data.cell.styles.valign = 'middle';
		}
	} as any);

	doc.save(filename);
};

// Simple Excel export without header (just data table)
export const exportToExcel = (
	data: any[],
	columns: ExportColumn[],
	filename = "export.xlsx"
) => {
	const formattedData = data.map((row) => {
		const result: Record<string, string> = {};
		columns.forEach((col) => {
			const value = row[col.dataIndex];
			result[col.title] = col.render ? col.render(value, row) : value ?? "";
		});
		return result;
	});

	const worksheet = XLSX.utils.json_to_sheet(formattedData);
	const workbook = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

	const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
	const blob = new Blob([excelBuffer], { type: "application/octet-stream" });

	saveAs(blob, filename);
};